import {
    type PatSnapCommonResponseData,
    PsHttpService,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    CronJob,
    CronJobInstructionCreateRequest,
    CronJobInstructionUpdateRequest,
    CronRun,
    CronRunNowResponse,
} from '../types';

export interface CronApi {
    listJobs(): Promise<CronJob[]>;
    getJob(jobId: string): Promise<CronJob>;
    createJob(data: CronJobInstructionCreateRequest): Promise<CronJob>;
    updateJob(jobId: string, data: CronJobInstructionUpdateRequest): Promise<CronJob>;
    deleteJob(jobId: string): Promise<void>;
    pauseJob(jobId: string): Promise<CronJob>;
    resumeJob(jobId: string): Promise<CronJob>;
    runNow(jobId: string): Promise<CronRunNowResponse>;
    listRuns(jobId: string, limit?: number, offset?: number): Promise<CronRun[]>;
    markSessionRead(sessionId: string): Promise<CronRun>;
}

export const CronApi = Symbol('CronApi');

@injectable()
export class CronApiImpl extends PsHttpService implements CronApi {
    private get baseURL() {
        return `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka/cronjobs`;
    }

    get options() {
        return {
            baseURL: this.baseURL,
            // baseURL: 'http://localhost:8000/api/eureka/cronjobs',
        };
    }

    listJobs(): Promise<CronJob[]> {
        return this.get<PatSnapCommonResponseData<CronJob[]>>({
            url: this.options.baseURL,
        }).then((response) => response as unknown as CronJob[]);
    }

    getJob(jobId: string): Promise<CronJob> {
        return this.get<PatSnapCommonResponseData<CronJob>>({
            url: `${this.options.baseURL}/${jobId}`,
        }).then((response) => response as unknown as CronJob);
    }

    createJob(data: CronJobInstructionCreateRequest): Promise<CronJob> {
        return this.post<PatSnapCommonResponseData<CronJob>>({
            url: this.options.baseURL,
            data,
        }).then((response) => response as unknown as CronJob);
    }

    updateJob(jobId: string, data: CronJobInstructionUpdateRequest): Promise<CronJob> {
        return this.put<PatSnapCommonResponseData<CronJob>>({
            url: `${this.options.baseURL}/${jobId}`,
            data,
        }).then((response) => response as unknown as CronJob);
    }

    async deleteJob(jobId: string): Promise<void> {
        await this.delete<PatSnapCommonResponseData<unknown>>({
            url: `${this.options.baseURL}/${jobId}`,
        });
    }

    pauseJob(jobId: string): Promise<CronJob> {
        return this.post<PatSnapCommonResponseData<CronJob>>({
            url: `${this.options.baseURL}/${jobId}/pause`,
        }).then((response) => response as unknown as CronJob);
    }

    resumeJob(jobId: string): Promise<CronJob> {
        return this.post<PatSnapCommonResponseData<CronJob>>({
            url: `${this.options.baseURL}/${jobId}/resume`,
        }).then((response) => response as unknown as CronJob);
    }

    runNow(jobId: string): Promise<CronRunNowResponse> {
        return this.post<PatSnapCommonResponseData<CronRunNowResponse>>({
            url: `${this.options.baseURL}/${jobId}/run-now`,
        }).then((response) => response as unknown as CronRunNowResponse);
    }

    listRuns(jobId: string, limit = 50, offset = 0): Promise<CronRun[]> {
        return this.get<PatSnapCommonResponseData<CronRun[]>>({
            url: `${this.options.baseURL}/${jobId}/runs`,
            params: { limit, offset },
        }).then((response) => response as unknown as CronRun[]);
    }

    markSessionRead(sessionId: string): Promise<CronRun> {
        return this.post<PatSnapCommonResponseData<CronRun>>({
            url: `${this.options.baseURL}/sessions/${sessionId}/read`,
        }).then((response) => response as unknown as CronRun);
    }
}
